import { ObjectToDraw } from '../types';
import SpriteAnimationRenderer from "../spriteAnimationRenderer";
import GameMap from './gameMap';
import P5 from 'p5';
import Bullet from '../entities/bullet';
import TextParticle from '../particles/textParticle';
import Tnt from '../entities/tnt';
import Smoke from '../particles/smoke';
import { moveWithCollisions } from './collision';

class Player{
    x: number;
    y: number;
    vx: number;
    vy: number;
    // Knockback impulse velocity. Added on top of the input movement every
    // frame and decays until negligible - the player has no control while
    // it's strong (they're being flung), but wall collision always applies.
    kbVx: number = 0;
    kbVy: number = 0;
    speed: number;

    smoothX: number;
    smoothY: number;

    keyW: boolean;
    keyA: boolean;
    keyS: boolean;
    keyD: boolean;
    touchStartX: number = 0;
    touchStartY: number = 0;
    isTouching: boolean = false;
    joystickX: number = 0;
    joystickY: number = 0;

    //mobile melee attack / interact button (right side, opposite the joystick)
    attackButtonX: number = 0;
    attackButtonY: number = 0;
    ATTACK_BUTTON_RADIUS: number = 50;
    attackTouchActive: boolean = false;
    attackButtonPressedTime: number = 0;

    currentDirection: number;
    isRunning: boolean;
    isShooting: boolean;
    isDead: boolean;
    enteredPortal: boolean;
    // Portal the player is currently standing in that asks for confirmation
    // before teleporting (portalData.ask). A "[SPACE] Travel to X" prompt is
    // shown while set, and the interact/attack key triggers the teleport -
    // same interaction pattern as the chest entity. null when not on one.
    pendingPortal: { world: string, tx: number, ty: number, ask: boolean } | null = null;
    // gameMap reference captured in update(), so attack() can teleport too.
    gameMap: GameMap | null = null;

    health: number;
    coins: number;

    animIdle: SpriteAnimationRenderer;
    animRunning: SpriteAnimationRenderer;
    animShooting: SpriteAnimationRenderer;
    animShootingHold: SpriteAnimationRenderer;
    animRunningAndShooting: SpriteAnimationRenderer;
    animRunningAndShootingHold: SpriteAnimationRenderer;
    animDead: SpriteAnimationRenderer;

    shootHoldStartTime: number;

    waypointX : number;
    waypointY : number;
    waypointShow : boolean;

    skin = "piti";
    hash: string;

    damageTimer: number = 0;
    damageEffectTime: number = 0;

    knockbackState: boolean = false;

    //UI images
    fullHeartImage: P5.Image;
    halfHeartImage: P5.Image;
    emptyHeartImage: P5.Image;
    coinImage: P5.Image;

    constructor(){
        this.hash = this.generateUUID();
        this.x = 256;
        this.y = 256;
        this.smoothX = this.x;
        this.smoothY = this.y;
        this.vx = 0;
        this.vy = 0;
        this.speed = 4;
        this.health = 100;
        this.coins = 0;
        this.currentDirection = 0;
        this.isRunning = false;
        this.isShooting = false;
        this.enteredPortal = false;

        this.waypointX = 5376;
        this.waypointY = 2816;
        this.waypointShow = false;

        this.keyW = false;
        this.keyA = false;
        this.keyS = false;
        this.keyD = false;

        this.animIdle = new SpriteAnimationRenderer();
        this.animIdle.load(`/assets/player/${this.skin}/idle`);

        this.animRunning = new SpriteAnimationRenderer();
        this.animRunning.load(`/assets/player/${this.skin}/running`);

        this.animShooting = new SpriteAnimationRenderer();
        this.animShooting.load(`/assets/player/${this.skin}/shooting`);
        this.animShooting.repeats = false;

        this.animShootingHold = new SpriteAnimationRenderer();
        this.animShootingHold.load(`/assets/player/${this.skin}/shootingHold`);

        this.animRunningAndShooting = new SpriteAnimationRenderer();
        this.animRunningAndShooting.load(`/assets/player/${this.skin}/runningAndShooting`);
        this.animRunningAndShooting.repeats = false;

        this.animRunningAndShootingHold = new SpriteAnimationRenderer();
        this.animRunningAndShootingHold.load(`/assets/player/${this.skin}/runningAndHoldingShot`);

        this.animDead = new SpriteAnimationRenderer();
        this.animDead.load(`/assets/player/${this.skin}/dead`);

        p5js.loadImage(`${staticServer}/assets/ui/player/full_heart.png`, (img) => {
            this.fullHeartImage = img;
        });

        p5js.loadImage(`${staticServer}/assets/ui/player/half_heart.png`, (img) => {
            this.halfHeartImage = img;
        });

        p5js.loadImage(`${staticServer}/assets/ui/player/empty_heart.png`, (img) => {
            this.emptyHeartImage = img;
        });

        p5js.loadImage(`${staticServer}/assets/ui/player/coin.png`, (img) => {
            this.coinImage = img;
        });
    }

    windowResized(): void {
        this.joystickX = -buffer.width/2 + 100;
        this.joystickY = buffer.height/2 - 200;

        //attack / interact button mirrors the joystick on the right side
        this.attackButtonX = buffer.width/2 - 100;
        this.attackButtonY = buffer.height/2 - 200;
    }

    goToDirection(dir: number) {
        // Reset all keys to false initially
        this.keyW = false;
        this.keyA = false;
        this.keyS = false;
        this.keyD = false;
    
        // Handle direction using a switch statement
        switch (dir) {
            case -1:
                // No need to change anything, all keys are already false
                break;
            case 0:
                this.keyS = true;
                break;
            case 2:
                this.keyA = true;
                break;
            case 4:
                this.keyW = true;
                break;
            case 6:
                this.keyD = true;
                break;
            case 1:
                this.keyS = true;
                this.keyA = true;
                break;
            case 3:
                this.keyA = true;
                this.keyW = true;
                break;
            case 5:
                this.keyW = true;
                this.keyD = true;
                break;
            case 7:
                this.keyD = true;
                this.keyS = true;
                break;
            default:
                console.warn(`Unknown direction: ${dir}`);
                break;
        }
    }

    generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            let r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    // Reset to a fresh-start state: full health, empty inventory, alive, no
    // residual motion or portal state. The caller sets the spawn position.
    // Shared by "Start new game" and respawn-after-death (see GameScreen).
    reset(){
        this.health = 100;
        this.coins = 0;
        this.isDead = false;
        this.vx = 0;
        this.vy = 0;
        this.kbVx = 0;
        this.kbVy = 0;
        this.knockbackState = false;
        this.enteredPortal = false;
        this.pendingPortal = null;
    }

    update(gameMap: GameMap){
        this.gameMap = gameMap;
        if(this.isDead){
            return;
        }

        if(this.health <= 0){
            this.health = 0;
            this.isDead = true;
        }

        // Diagonal movement is normalized (speed * 1/sqrt(2) per axis) so moving
        // diagonally isn't faster than moving along a single axis.
        let diagonalSpeed = this.speed * 0.7071067811865476;

        // Movement velocity from input. While a knockback impulse is strong
        // the player has no control (they're being flung).
        let moveVx = 0;
        let moveVy = 0;

        if(!this.knockbackState){
            if(this.keyA && this.keyW){
                this.currentDirection = 3;
                moveVx = -diagonalSpeed;
                moveVy = -diagonalSpeed;
            }else if(this.keyA && this.keyS){
                this.currentDirection = 1;
                moveVx = -diagonalSpeed;
                moveVy = diagonalSpeed;
            }else if(this.keyD && this.keyW){
                this.currentDirection = 5;
                moveVx = diagonalSpeed;
                moveVy = -diagonalSpeed;
            }else if(this.keyD && this.keyS){
                this.currentDirection = 7;
                moveVx = diagonalSpeed;
                moveVy = diagonalSpeed;
            }else if(this.keyW){
                this.currentDirection = 4;
                moveVy = -this.speed;
            }else if(this.keyA){
                this.currentDirection = 2;
                moveVx = -this.speed;
            }else if(this.keyS){
                this.currentDirection = 0;
                moveVy = this.speed;
            }else if(this.keyD){
                this.currentDirection = 6;
                moveVx = this.speed;
            }
        }

        // Total velocity = input movement + knockback impulse.
        this.vx = moveVx + this.kbVx;
        this.vy = moveVy + this.kbVy;

        // Decay the knockback impulse until it's negligible.
        this.kbVx *= 0.9;
        this.kbVy *= 0.9;
        if(p5js.max(p5js.abs(this.kbVx), p5js.abs(this.kbVy)) < 1){
            this.kbVx = 0;
            this.kbVy = 0;
            this.knockbackState = false;
        }

        this.isRunning = (moveVx != 0 || moveVy != 0);

        let collisionOffset = 5; //how far the player is kept away from walls

        // Move through the shared collision module (substepped), so even a
        // strong knockback slides along walls instead of clipping through them.
        let moveResult = moveWithCollisions(gameMap, this.x, this.y, this.vx, this.vy, collisionOffset);
        let tmpX = moveResult.x;
        let tmpY = moveResult.y;

        // React to the non-wall areas we passed through during the move.
        let inPortal = false;
        let portalToEnter: { world: string, tx: number, ty: number, ask: boolean } | null = null;
        for(let area of moveResult.overlappingAreas){
            if(area.type == "portal"){
                inPortal = true;
                if(area.portalData){
                    if(area.portalData.ask){
                        // Wait for the player to confirm with the interact key
                        // (see renderUI's prompt and attack()).
                        this.pendingPortal = area.portalData;
                    }else if(!this.enteredPortal){
                        // Auto-teleport on entry; guarded so it fires once.
                        portalToEnter = area.portalData;
                    }
                }
            }else if(area.type == "damage"){

                if(this.damageTimer < p5js.millis()){
                    this.damageTimer = p5js.millis() + 500;
                    this.damageEffectTime = p5js.millis() + 150;
                    if(area.damageData){
                        this.health -= area.damageData.damage;
                        particles.push(new TextParticle(this.x, this.y-25, `-${area.damageData.damage}hp`));
                    }else{
                        this.health -= 5;
                        particles.push(new TextParticle(this.x, this.y-25, "-5hp"));
                    }
                }
            }else if(area.type == "trigger"){
                this.waypointShow = !this.waypointShow;
            }else if(area.type == "door"){

            }
        }
        this.enteredPortal = inPortal;
        if(!inPortal){
            this.pendingPortal = null; // left the portal before confirming
        }

        // let isPlayerInPortal = false;

        // for(let portal of chunk.data.portals){
        //     let portalX = (chunk.tileX * Chunk.CHUNK_SIZE) - tmpX + portal.x;
        //     let portalY = (chunk.tileY * Chunk.CHUNK_SIZE) - tmpY + portal.y;

        //     if(0 > portalX && 0 < portalX + portal.w && 0 > portalY && 0 < portalY + portal.h){
        //         isPlayerInPortal = true;
        //         if(!this.enteredPortal){
        //             tmpX = portal.targetX;
        //             tmpY = portal.targetY;
        //             console.log("Entered portal", portal.targetX, portal.targetY);
        //             console.log("Player position", this.x, this.y);
        //             gameMap.loadWorld(portal.world, portal.targetX, portal.targetY);
        //         }
        //     }
        // }
        // this.enteredPortal = isPlayerInPortal;

        this.x = tmpX;
        this.y = tmpY;

        // Auto-teleport portals resolve here, after the position is committed,
        // so the teleport target isn't overwritten by the move above.
        if(portalToEnter){
            this.teleportThroughPortal(portalToEnter);
        }

        this.smoothX = this.x; //p5js.lerp(this.smoothX, this.x, 0.1);
        this.smoothY = this.y; //p5js.lerp(this.smoothY, this.y, 0.1);

        gameMap.posX = p5js.floor(this.smoothX);
        gameMap.posY = p5js.floor(this.smoothY);
        

        // if(this.health < 100){
        //     this.health += 0.01;
        // }
    }

    hit(damage: number){
        let d = damage / 5;
        this.health -= d;
        if(this.health < 0){
            this.health = 0;
        }
        particles.push(new TextParticle(this.x, this.y-25, `-${d}hp`));
        this.damageEffectTime = p5js.millis() + 150;

        if(this.health <= 0){
            this.isDead = true;
        }
    }

    knockback(x: number, y: number){
        this.knockbackState = true;
        this.kbVx = x;
        this.kbVy = y;
    }

    render(objectsToDraw: ObjectToDraw[]){

        let px = p5js.floor(this.x - this.smoothX);
        let py = p5js.floor(this.y - this.smoothY);

        if(showDebug){
            buffer.noStroke();
            buffer.fill(255, 0, 0);
            buffer.ellipse(0-this.x, 0-this.y, 4, 4); // Draw center of the map

            buffer.stroke(255);
            buffer.line(-20, 0, 20, 0);
            buffer.line(0, -20, 0, 20);
        }

        buffer.noStroke();
        buffer.fill(0, 100);
        buffer.ellipse(px, py, 50, 25);

        //this.isShooting = p5js.mouseIsPressed;

        if(this.isDead){
            this.animDead.render(objectsToDraw, px, py, this.currentDirection);
        }else if(this.isShooting){
            let angle = 0;
            if(this.isRunning){
                angle = this.currentDirection * p5js.PI/4 + p5js.PI/2; 
            }else{
                angle = p5js.atan2((p5js.mouseY/pixelSize)+75 - buffer.height/2, p5js.mouseX/pixelSize - buffer.width/2);
                this.currentDirection = p5js.int(p5js.map(angle + 0.3, 0, p5js.TWO_PI, 0, 8) + 6) % 8;
            }


            if(!this.isRunning){
                if(this.animShooting.playing){
                    this.animShooting.render(objectsToDraw, px, py, this.currentDirection);
                    this.animRunningAndShooting.frame = this.animShooting.frame;
                }else{
                    this.animShootingHold.render(objectsToDraw, px, py, this.currentDirection);
                }
            }else{
                if(this.animRunningAndShooting.playing){
                    this.animRunningAndShooting.render(objectsToDraw, px, py, this.currentDirection);
                    this.animShooting.frame = this.animRunningAndShooting.frame;
                }else{
                    this.animRunningAndShootingHold.render(objectsToDraw, px, py, this.currentDirection);
                }
            }
        }else{
            if(this.isRunning){
                this.animRunning.render(objectsToDraw, px, py, this.currentDirection);
            }else{
                this.animIdle.render(objectsToDraw, px, py, this.currentDirection);
            }
        }
    }

    renderUI(){

        //teleport confirmation prompt while standing in an "ask" portal
        //(same style as the chest's "[SPACE] Open" prompt)
        if(this.pendingPortal){
            let prompt = (isMobile ? "[ATTACK]" : "[SPACE]") + ` Travel to "${this.pendingPortal.world}"`;
            let py = -40;

            buffer.textSize(11);
            buffer.textAlign(buffer.CENTER, buffer.CENTER);
            let tw = buffer.textWidth(prompt);

            buffer.noStroke();
            buffer.fill(0, 150);
            buffer.rect(-tw/2-5, py-9, tw+10, 18);

            buffer.fill(255);
            buffer.text(prompt, 0, py);
        }

        // buffer.fill(255);
        // buffer.noStroke();
        // buffer.textSize(20);
        // buffer.textAlign(buffer.CENTER, buffer.TOP);
        // buffer.text("Coins: " + this.coins, 0, -buffer.height/2+10);
        // if(showDebug){
        //     buffer.fill(255);
        //     buffer.noStroke();
        //     buffer.textSize(10);
        //     buffer.textAlign(buffer.CENTER, buffer.BOTTOM);
        //     buffer.text("" + this.hash, 0, -110);
        // }

        if(this.waypointShow){
            let xOnScreen = this.waypointX - this.x;
            let yOnScreen = this.waypointY - this.y;

            //check if it is on the screen render area
            if(xOnScreen > -buffer.width/2 && xOnScreen < buffer.width/2 && yOnScreen > -buffer.height/2 && yOnScreen < buffer.height/2){
                buffer.stroke(255);
                buffer.strokeWeight(2);
                buffer.noFill();
                buffer.ellipse(xOnScreen, yOnScreen, 10, 10);

                buffer.noStroke();
                buffer.fill(255);
                buffer.textSize(10);
                buffer.textAlign(buffer.CENTER, buffer.BOTTOM);
                buffer.text("Waypoint", xOnScreen, yOnScreen - 10);
            }else{
                // position for off-screen indicator
                let halfWidth = buffer.width / 2;
                let halfHeight = buffer.height / 2;
                let offset = 15;

                let xBoundary = halfWidth - offset;
                let yBoundary = halfHeight - offset;

                let intersectX, intersectY;

                // Check intersections with screen bounds with offset
                if (Math.abs(xOnScreen / yOnScreen) > Math.abs(xBoundary / yBoundary)) {
                    intersectX = xBoundary * Math.sign(xOnScreen);
                    intersectY = intersectX * yOnScreen / xOnScreen;
                } else {
                    intersectY = yBoundary * Math.sign(yOnScreen);
                    intersectX = intersectY * xOnScreen / yOnScreen;
                }

                buffer.stroke(255);
                buffer.strokeWeight(2);
                buffer.noFill();
                buffer.ellipse(intersectX, intersectY, 10, 10); // Display off-screen indicator
            }
        }


        //draw health
        if(this.fullHeartImage && this.halfHeartImage && this.emptyHeartImage){
            // Draw hearts, each hart is 20hp, half heart is 10hp
            let fullHearts = Math.floor(this.health / 20);
            let halfHearts = Math.floor((this.health % 20) / 10);
            let emptyHearts = 5 - fullHearts - halfHearts;
            let heartSize = this.fullHeartImage.width + 2;
            let pos = p5js.floor(-buffer.width/2 + 10);
            for(let i = 0; i < fullHearts; i++){
                buffer.image(this.fullHeartImage, pos, p5js.floor(-buffer.height/2+10));
                pos += heartSize;
            }

            for(let i = 0; i < halfHearts; i++){
                buffer.image(this.halfHeartImage, pos, p5js.floor(-buffer.height/2+10));
                pos += heartSize;
            }

            for(let i = 0; i < emptyHearts; i++){
                buffer.image(this.emptyHeartImage, pos, p5js.floor(-buffer.height/2+10));
                pos += heartSize;
            }
        }

        //draw coins
        if(this.coinImage){
            buffer.image(this.coinImage, p5js.floor(buffer.width/2-42), p5js.floor(-buffer.height/2+10));
            buffer.fill(255);
            buffer.noStroke();
            buffer.textSize(20);
            buffer.textAlign(buffer.RIGHT, buffer.CENTER);
            buffer.text(this.coins, buffer.width/2-47, -buffer.height/2+26);
        }

        // draw touch control indicator
        if(isMobile){
            buffer.noStroke();
            if(this.isTouching){
                buffer.fill(255, 100);
            }else{
            buffer.fill(255, 50);
            }
            buffer.ellipse(this.joystickX, this.joystickY, 100, 100);

            if(this.isTouching){
                const touch = p5js.touches[0] as any;
                let endPosX = touch.x/pixelSize-buffer.width/2;
                let endPosY = touch.y/pixelSize-buffer.height/2;

                let angle = Math.atan2(endPosY - this.joystickY, endPosX - this.joystickX);
                let distance = p5js.dist(this.joystickX, this.joystickY, endPosX, endPosY)/4;
                if(distance > 35){
                    distance = 35;
                }

                buffer.ellipse(this.joystickX + p5js.cos(angle) * distance, this.joystickY + p5js.sin(angle) * distance, 30, 30);
            }else{
                buffer.ellipse(this.joystickX, this.joystickY, 30, 30);
            }

            //melee attack / interact button (right side)
            let attackPressed = this.attackTouchActive || this.attackButtonPressedTime + 150 > p5js.millis();
            buffer.noStroke();
            if(attackPressed){
                buffer.fill(255, 100);
            }else{
                buffer.fill(255, 50);
            }
            buffer.ellipse(this.attackButtonX, this.attackButtonY, 80, 80);

            //simple sword icon (blade + crossguard)
            buffer.stroke(255, attackPressed ? 220 : 120);
            buffer.strokeWeight(3);
            buffer.line(this.attackButtonX-10, this.attackButtonY+10, this.attackButtonX+12, this.attackButtonY-12);
            buffer.line(this.attackButtonX-11, this.attackButtonY+1, this.attackButtonX-1, this.attackButtonY+11);
            buffer.noStroke();
        }

        if(this.damageEffectTime > p5js.millis()){ //damage effect
            let val = this.damageEffectTime - p5js.millis();
            if(val > 100){
                val = 100;
            }
            buffer.fill(255, 0, 0, val);
            buffer.noStroke();
            buffer.rect(-buffer.width/2, -buffer.height/2, buffer.width, buffer.height);
        }
    }

    getDebugText(){
        return "Player position: " + Math.round(this.x) + ", " + Math.round(this.y) + "\n";
    }

    startShooting(){
        if(this.isDead){
            return;
        }
        this.isShooting = true;
        this.animShooting.start(0);
        this.animRunningAndShooting.start(0);
        this.shootHoldStartTime = p5js.millis();
    }

    endShooting(){
        if(this.isShooting === true){
            this.isShooting = false;
            if((p5js.millis() - this.shootHoldStartTime - 400)/25.0 > 0){ //400ms default hold time 
                let angle = p5js.atan2((p5js.mouseY/pixelSize)+75 - buffer.height/2, p5js.mouseX/pixelSize - buffer.width/2);
                let distance = p5js.dist(0, -75, (p5js.mouseX/pixelSize) - buffer.width/2, (p5js.mouseY/pixelSize) - buffer.height/2);
                entities.push(new Bullet(this.x, this.y-75, angle, 20));

                // entities.push(new Bullet(this.x, this.y-75, angle-0.1, 20));
                // entities.push(new Bullet(this.x, this.y-75, angle-0.05, 20));
                // entities.push(new Bullet(this.x, this.y-75, angle+0.05, 20));
                // entities.push(new Bullet(this.x, this.y-75, angle+0.1, 20));

                //entities.push(new Tnt(this.x, this.y-75, this.x + (p5js.cos(angle) * distance), this.y-75 + (p5js.sin(angle) * distance)));
            }
        }
    }

    //attack with melee weapon
    attack(){
        if(this.isDead){
            return;
        }

        //damage enemies in range
        let attackRange = 150;
        let attackDamage = 5;
        for(let entity of entities){
            if(entity.damagable){
                let distance = p5js.dist(this.x, this.y, entity.x, entity.y);
                if(distance < attackRange){
                    entity.hit(attackDamage);
                    if(entity.knockback){
                        //knock the entity away from the player
                        let angle = p5js.atan2(entity.y - this.y, entity.x - this.x);
                        entity.knockback(p5js.cos(angle) * 12, p5js.sin(angle) * 12);
                    }
                    particles.push(new Smoke(entity.x, entity.y, 10));
                }
            }

            //interact with entities in range (open chests etc.)
            if(entity.interact){
                let distance = p5js.dist(this.x, this.y, entity.x, entity.y);
                if(distance < (entity.interactRange ?? 80)){
                    entity.interact();
                }
            }
        }

        //confirm a teleport when standing in an "ask" portal
        if(this.pendingPortal){
            this.teleportThroughPortal(this.pendingPortal);
            this.pendingPortal = null;
        }
    }

    // Moves the player through a portal, loading the target world first if it
    // differs from the current one. enteredPortal is set so the destination
    // (which may itself overlap a portal) doesn't immediately re-trigger.
    teleportThroughPortal(pd: { world: string, tx: number, ty: number, ask: boolean }){
        if(this.gameMap && pd.world && pd.world != this.gameMap.worldName){
            this.gameMap.loadWorld(pd.world, pd.tx, pd.ty);
        }
        this.x = pd.tx;
        this.y = pd.ty;
        this.smoothX = this.x;
        this.smoothY = this.y;
        if(this.gameMap){
            this.gameMap.posX = p5js.floor(this.x);
            this.gameMap.posY = p5js.floor(this.y);
        }
        this.enteredPortal = true;
    }

    keyPressed(){
        //console.log("Key pressed");
        // Use WASD to move the player
        let lowerKey = p5js.key.toLowerCase();
        if(lowerKey == 'w'){
            this.keyW = true;
        }
        if(lowerKey == 'a'){
            this.keyA = true;
        }
        if(lowerKey == 's'){
            this.keyS = true;
        }
        if(lowerKey == 'd'){
            this.keyD = true;
        }
        if(lowerKey == ' '){
            this.attack();
        }
    }

    keyReleased(){
        //console.log("Key released");
        // Use WASD to move the player
        let lowerKey = p5js.key.toLowerCase();
        if(lowerKey == 'w'){
            this.keyW = false;
        }
        if(lowerKey == 'a'){
            this.keyA = false;
        }
        if(lowerKey == 's'){
            this.keyS = false;
        }
        if(lowerKey == 'd'){
            this.keyD = false;
        }
    }

    mousePressed(){
        //console.log("Mouse pressed");
        this.startShooting();
    }

    mouseReleased(){
        //console.log("Mouse released");
        this.endShooting();
    }

    //true when any active touch is inside the attack / interact button
    isTouchOnAttackButton(): boolean {
        for(let i = 0; i < p5js.touches.length; i++){
            const t = p5js.touches[i] as any;
            let tx = t.x/pixelSize-buffer.width/2;
            let ty = t.y/pixelSize-buffer.height/2;
            if(p5js.dist(tx, ty, this.attackButtonX, this.attackButtonY) < this.ATTACK_BUTTON_RADIUS){
                return true;
            }
        }
        return false;
    }

    touchStarted(){
        //console.log("Touch started");

        //melee attack / interact button (right side of the screen)
        if(isMobile && this.isTouchOnAttackButton()){
            if(!this.attackTouchActive){
                this.attackTouchActive = true;
                this.attackButtonPressedTime = p5js.millis();
                this.attack();
            }
            return;
        }

        const touch = p5js.touches[0] as any;

        this.touchStartX = touch.x/pixelSize-buffer.width/2;
        this.touchStartY = touch.y/pixelSize-buffer.height/2;

        if(p5js.dist(this.touchStartX, this.touchStartY, this.joystickX, this.joystickY) < 50){
            this.isTouching = true;
            return;
        }

        this.startShooting();
    }

    touchEnded(){
        //console.log("Touch ended");

        //release the attack button once no touch remains on it
        if(this.attackTouchActive && !this.isTouchOnAttackButton()){
            this.attackTouchActive = false;
            return;
        }

        if(this.isTouching){
            player.goToDirection(-1);
            this.isTouching = false;
        }

        if(p5js.dist(this.touchStartX, this.touchStartY, this.joystickX, this.joystickY) < 50){
            //this.isTouching = true;
            return;
        }

        this.endShooting();
    }

    touchMoved(){
        // control the player with touch
        const touch = p5js.touches[0] as any;
        
        if(touch && this.isTouching){
            let endPosX = touch.x/pixelSize-buffer.width/2;
            let endPosY = touch.y/pixelSize-buffer.height/2;
            // calculate the angle between the start and current touch position
            const angle = Math.atan2(endPosY - this.joystickY, endPosX - this.joystickX);

            //determine the direction based on the angle (8 directions)
            let direction = p5js.int(p5js.map(angle + 0.3, 0, p5js.TWO_PI, 0, 8) + 6) % 8;

            //move the player
            player.goToDirection(direction);
        }
    }

    mouseWheel(event: any){

    }
}

export default Player;